// The button semantics (design manual Ch08 §3.3), in one place. Before
// 2026-09-21 the SPA carried 45 hand-rolled class sets on <button>; the six
// meanings below are what those sets were reaching for.
//
//   primary    — the one action the surface is for. Filled, action colour.
//   secondary  — everything else. Outlined.
//   danger     — destroys or discards. Outlined in the error colour; `filled`
//                only for the confirming step of a destructive flow.
//   warning    — proceeds past a caution. Filled, warning colour.
//   outline    — an action that opens or reveals rather than commits. Outlined
//                in the action colour.
//   ghost      — an icon or a row control that must not compete with content.
//   link       — an inline textual action.
//   chrome     — a control on the black header bar, the one dark surface in
//                both themes.
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// eslint-disable-next-line react-refresh/only-export-components -- the class recipe is exported beside the component, as shadcn does
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium transition-colors ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tres-action focus-visible:ring-offset-2 ' +
    'disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-tres-action text-white hover:bg-tres-action/90',
        secondary: 'border border-border bg-card text-tres-text-primary hover:bg-muted',
        danger: 'border border-tres-error text-tres-error hover:bg-tres-error/10',
        dangerFilled: 'bg-tres-error text-white hover:bg-tres-error/90',
        warning: 'bg-tres-warning text-white hover:bg-tres-warning/90',
        outline: 'border border-tres-action text-tres-action hover:bg-tres-submenu-bg',
        ghost: 'rounded text-tres-text-secondary hover:bg-muted hover:text-tres-text-primary',
        chrome: 'border border-white/20 text-white/85 hover:bg-white/10',
        link: 'rounded-none p-0 font-normal text-tres-action underline underline-offset-2 hover:text-tres-action/80',
      },
      size: {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-3 py-1.5 text-sm',
        lg: 'px-4 py-2 text-sm',
        icon: 'p-1 text-sm',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render the child element (a link, say) with the button's look. */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        // A bare <button> inside a form submits it; every button here is an
        // action unless the caller says otherwise.
        type={asChild ? undefined : (type ?? 'button')}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';
